import React, { useState } from 'react';
import { CameraIcon } from 'lucide-react';
import { Container, Section } from '../components/ui/Section';
import { PageHeader } from '../components/ui/PageHeader';
import { PhotoGallery } from '../components/photos/PhotoGallery';
import { EmptyState } from '../components/ui/EmptyState';
import { ButtonExternal } from '../components/ui/Button';
import { DoodleStar } from '../components/brand/Doodles';
import { albumMeta, featuredPhoto, libraryPhotos, type AlbumName } from '../data/photos';
import { externalLinks } from '../data/site';
type Filter = 'All' | AlbumName;
const filters: Filter[] = ['All', 'Summer ’26', 'Fall ’26', 'Spring ’27'];
export function Photos() {
  const [filter, setFilter] = useState<Filter>('All');
  const visible = filter === 'All' ? libraryPhotos : libraryPhotos.filter(photo => photo.album === filter);
  const emptyAlbum = filter !== 'All' ? albumMeta.find(album => album.name === filter) : undefined;
  return <>
      <PageHeader eyebrow="Photos" title="Life at BCM" intro="Explore real moments from worship nights, campus outreach, recreation, community groups, and time spent together." data-id="element-564" data-mp="2al590m3wecq0" />

      {/* Featured photo */}
      <Section className="bg-[#fffdf6] py-14 sm:py-18" labelledBy="featured-heading" data-id="element-565" data-mp="2kkcll72t63c2">
        <Container className="grid items-center gap-8 lg:grid-cols-[1.15fr_0.85fr] lg:gap-12" data-id="element-566" data-mp="2fjou6kao3khb">
          <figure className="relative overflow-hidden rounded-[20px] border-2 border-brand-navy bg-brand-blue" data-id="element-567" data-mp="2tjhhe5mzjikj">
            {featuredPhoto.src ? <img src={featuredPhoto.src} alt={featuredPhoto.alt} className="aspect-[16/10] w-full object-cover" data-id="element-568" data-mp="2okmsqn2c6zsi" /> : <div className="flex aspect-[16/10] w-full flex-col items-center justify-center gap-3 px-6 text-center" data-id="element-569" data-mp="2ybhoqj5dxdr5">
                <CameraIcon aria-hidden="true" className="h-8 w-8 text-brand-navy" data-id="element-570" data-mp="3c84m2n1925xl" />
                <p className="font-display text-xl font-bold text-brand-navy" data-id="element-571" data-mp="386suibytfamr">
                  {featuredPhoto.subject}
                </p>
                <p className="font-meta text-[12px] uppercase tracking-[0.14em] text-brand-navy" data-id="element-572" data-mp="3kandad03fk20">
                  {featuredPhoto.album} · real BCM photo
                </p>
              </div>}
            <figcaption className="border-t-2 border-brand-navy bg-white px-5 py-3 font-meta text-[13px] text-ink" data-id="element-573" data-mp="3u18abw04cgit">
              {featuredPhoto.caption ?? featuredPhoto.subject}
            </figcaption>
          </figure>

          <div className="relative" data-id="element-574" data-mp="6uz5hti2rgzd">
            <DoodleStar className="absolute -left-3 -top-6 h-9 w-9" color="var(--bcm-yellow)" data-id="element-575" data-mp="6y3k7kt61v4a" />
            <h2 id="featured-heading" className="font-serif text-2xl font-semibold leading-snug text-brand-navy sm:text-3xl" data-id="element-576" data-mp="btits0k554uw">
              2026–27 BCM Photos
            </h2>
            <p className="mt-4 font-sans text-[16px] leading-relaxed text-ink" data-id="element-577" data-mp="2iltamkgizak0">
              Every photo on this page comes from the BCM archive. Captions are only added when the
              source folder confirms what the photo shows.
            </p>
            <ul className="mt-5 space-y-2" data-id="element-578" data-mp="uyfby02mnn1z">
              {albumMeta.map(album => <li key={album.name} className="font-meta text-[13px] text-ink" data-id="element-579" data-mp="qz6iyxfevt7h">
                  <span className="font-semibold text-brand-navy" data-id="element-580" data-mp="m03djbvirkyz">{album.name}</span> —{' '}
                  {album.status}
                </li>)}
            </ul>
            <ButtonExternal href={externalLinks.photoAlbum} className="mt-7" data-id="element-581" data-mp="1eo8y9ymkp9yt">
              Open Full Photo Album
            </ButtonExternal>
          </div>
        </Container>
      </Section>

      {/* Gallery */}
      <Section className="bcm-paper py-14 sm:py-18" labelledBy="gallery-heading" data-id="element-582" data-mp="14ql1721hvqdh">
        <Container data-id="element-583" data-mp="3ueu3xl0cqy16">
          <h2 id="gallery-heading" className="font-display text-3xl font-extrabold leading-tight tracking-tight text-brand-navy sm:text-4xl" data-id="element-584" data-mp="3ufdhkpiyz09e">
            Browse by semester
          </h2>

          <div role="group" aria-label="Filter photos by semester" className="mt-6 flex flex-wrap gap-2" data-id="element-585" data-mp="1h5rif5ahi6xd">
            {filters.map(option => {
            const isActive = filter === option;
            return <button key={option} type="button" aria-pressed={isActive} onClick={() => setFilter(option)} className={`inline-flex min-h-[44px] items-center rounded-full border-2 border-brand-navy px-5 font-meta text-[13px] font-semibold transition-colors ${isActive ? 'bg-brand-navy text-white' : 'bg-white text-brand-navy hover:bg-brand-tan'}`} data-id="element-586" data-mp="1d4xuibx1ks4h">
                  {option}
                </button>;
          })}
          </div>

          <div className="mt-8" data-id="element-587" data-mp="khl7y1qvskb6">
            {visible.length > 0 ? <PhotoGallery photos={visible} data-id="element-588" data-mp="cxiy1pa0escf" /> : <EmptyState label={emptyAlbum?.name ?? 'Coming soon'} title="No photos in this folder yet" copy={emptyAlbum?.message ?? 'Photos will be added to this folder as the semester goes on.'} tone="white" data-id="element-589" data-mp="cvzhryocshan">
                <ButtonExternal href={externalLinks.photoAlbum} variant="secondary" data-id="element-590" data-mp="3peo7ojmfb76f">
                  Open Full Photo Album
                </ButtonExternal>
              </EmptyState>}
          </div>

          <p className="mt-6 font-meta text-[12.5px] text-ink" data-id="element-591" data-mp="2zwid0kvywre">
            Photos load as you scroll. Select any photo to open a larger view — use the arrow keys to
            move between photos and Escape to close.
          </p>
        </Container>
      </Section>
    </>;
}